import React, { ChangeEvent, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { update } from 'serializr';

import useViewModel from '@vromo/hooks/useViewModel';
import { TaskBlueprint } from '@vromo/models';

import { ModalHeader, ModalContent, ModalActions } from '@vromo/ui/components/Modal';
import Button from '@vromo/ui/components/Button';
import Title from '@vromo/ui/components/Title';
import RadioGroup from '@vromo/ui/components/Radio';
import Input from '@vromo/ui/components/Input';
import Checkbox from '@vromo/ui/components/Checkbox';
import { TaskConstraint } from '@vromo/models';
import { css } from 'emotion';
import Select from '@vromo/ui/components/Select2';
import { GeozoneType } from '@vromo/models';
import { validName } from '@vromo/dashboard/src/blueprints/utils';
import Alert from '@vromo/ui/components/Alert';
import first from 'lodash/first';
import convertDistance from '@vromo/utils/convertDistance';
import rootStore from '../stores/RootStore';
import theme from '@vromo/ui/components/theme';
import { runInAction } from 'mobx';
import isNil from 'lodash/isNil';

export const CONSTRAINT_OPTIONS: Array<{
    label: string;
    description: string;
    value: TaskConstraint;
}> = [
        {
            label: 'Photo Upload',
            description: 'The driver must upload a photo to complete this task.',
            value: 'photo'
        },
        {
            label: 'Signature',
            description: 'The driver must provide a signature to complete this task.',
            value: 'signature'
        }
    ];

type Props = {
    taskTemplate: TaskBlueprint;
    cancel: () => void;
    isNew: boolean;
    addTask: (newTask: TaskBlueprint) => void;
};

export default observer(function BlueprintTaskEditor({
    taskTemplate,
    cancel,
    isNew,
    addTask
}: Props) {
    const { viewStore } = rootStore;

    const [editedTaskTemplate] = useViewModel(taskTemplate);
    const [completionMethod, setCompletionMethod] = useState(() =>
        editedTaskTemplate.constraints.has('manual') ? 'manual' : 'none'
    );
    const [taskConstraints, setTaskConstraints] = useState(() =>
        editedTaskTemplate.constraints.has('region') ? 'region' : 'none'
    );
    const [taskName, setTaskName] = useState(
        () => (editedTaskTemplate.zone && editedTaskTemplate.zone.name) ?? ''
    );
    const [taskType, setTaskType] = useState(
        () => (editedTaskTemplate.zone && editedTaskTemplate.zone.type) ?? 'arrival'
    );
    const [hasDefaultGeofence, setHasDefaultGeofence] = useState(
        () =>
            editedTaskTemplate.constraints.has('manual') &&
            !editedTaskTemplate.constraints.has('region')
    );
    const [taskRadius, setTaskRadius] = useState(() =>
        convertDistance((editedTaskTemplate.zone && editedTaskTemplate.zone.radius) ?? 50, false)
    );
    const [sourcesList] = useState(() => [
        { label: 'Pickup', value: 0 },
        { label: 'Dropoff', value: 1 }
    ]);

    const [errors, setErrors] = useState<Array<any>>([]);

    function saveTask() {
        const errors = validateBlueprintTask(editedTaskTemplate);
        setErrors(errors);

        if (errors.length !== 0) return;
        if (isNew) {
            runInAction(() => update(editedTaskTemplate, {
                zone: {
                    ...editedTaskTemplate.zone,
                    address: '{{task.address}}',
                }
            }));
            addTask(editedTaskTemplate);
        } else {
            runInAction(() => update(taskTemplate, { ...editedTaskTemplate }));
        }

        cancel();
    }

    function updateOffset(offset: string) {
        const offsetInt = parseInt(offset);
        runInAction(() => update(editedTaskTemplate, {
            offset: Number.isNaN(offsetInt) ? null : offsetInt * 60 * 1000
        }));
    }

    function updateConstraints(constraint: string) {
        runInAction(() => constraint === 'region'
            ? editedTaskTemplate.constraints.add('region')
            : editedTaskTemplate.constraints.delete('region'));

        setTaskConstraints(constraint);
    }

    function updateCompletionMethod(method: TaskConstraint) {
        runInAction(() => method === 'manual'
            ? editedTaskTemplate.constraints.add('manual')
            : editedTaskTemplate.constraints.delete('manual'));

        setCompletionMethod(method);
    }

    function updateSource(newSource: string) {
        runInAction(() => update(editedTaskTemplate, {
            source: parseInt(newSource)
        }));
    }

    function updateName(newName: string) {
        setTaskName(newName);
        runInAction(() => editedTaskTemplate.zone.name = newName);
    }

    function updateRadius(newRadius: string) {
        const radius = parseInt(newRadius);

        setTaskRadius(convertDistance(radius, viewStore.imperialUnits));
        runInAction(() => editedTaskTemplate.zone.radius = radius);
    }

    function updateTimeframe(type: string) {
        runInAction(() => update(editedTaskTemplate, {
            timeframe: type
        }));

        if (type === 'mask') {
            updateOffset('0');
        }
    }

    function updateTaskType(type: GeozoneType) {
        setTaskType(type);
        runInAction(() => editedTaskTemplate.zone.type = type);
    }

    function updateGeofenceVisibility(isDefaultGeofence: boolean) {
        runInAction(() => {
            if (isDefaultGeofence) {
                updateCompletionMethod('manual');
                updateConstraints('none');
            }
        })

        setHasDefaultGeofence(isDefaultGeofence);
    }

    function validateBlueprintTask(task: TaskBlueprint): any[] {
        const taskErrors = [];
        if (isNil(task.source)) {
            taskErrors.push({
                field: 'source',
                message: 'Please select an input for this task.'
            });
        }

        if (!task.zone || !task.zone.name) {
            taskErrors.push({
                field: 'missing-name',
                message: 'Please enter a task name.'
            });
        }

        return taskErrors;
    }

    function validateName(name: string) {
        if (!validName(name)) {
            setErrors(
                errors.concat([
                    {
                        field: 'name',
                        message:
                            'Task name is invalid - valid name parameters are {{task.name}} and {{task.address}}.'
                    }
                ])
            );
        } else {
            setErrors(errors.filter((it) => it.field !== 'name'));
        }
    }

    return (
        <>
            <ModalHeader>
                <Title>{isNew ? `New Output Task` : `Edit Output Task`}</Title>
            </ModalHeader>
            <ModalContent>
                {errors.length !== 0 && <Alert danger>{first(errors).message}</Alert>}
                <div
                    className={css`
                        display: flex;
                        flex-direction: column;
                        flex: 1;

                        & > div {
                            margin: 10px 0px;
                        }
                    `}>
                    <div>
                        <label>Which input will be applied to this task?</label>
                        <Select
                            value={sourcesList[editedTaskTemplate.source!]}
                            onChange={(selected: any) => updateSource(selected.value)}
                            options={sourcesList}
                            placeHolder="Select an input source"
                        />
                    </div>

                    <div>
                        <Input
                            label="What is the name of this task?"
                            value={taskName}
                            onBlur={(_: any) => validateName(taskName)}
                            placeholder="Arrive at {{task.name}}"
                            onChange={(e: any) => updateName(e.target.value)}
                            smalllabel={`- Try using {{task.name}} or {{task.address}}`}
                        />
                    </div>

                    <div>
                        <label>What type of task is this?</label>
                        <RadioGroup
                            name={'task-type'}
                            options={[
                                {
                                    label: 'Arrival',
                                    value: 'arrival'
                                },
                                {
                                    label: 'Departure',
                                    value: 'departure'
                                }
                            ]}
                            direction={'row'}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateTaskType(e.target.value as GeozoneType)
                            }
                            checked={taskType}
                        />
                    </div>

                    <div style={{ marginBottom: '0px' }}>
                        <label>How do you want to set up the geofence?</label>
                        <RadioGroup
                            name={'task-radius'}
                            options={[
                                {
                                    label: 'Use the default settings',
                                    value: 'true'
                                },
                                {
                                    label: 'Customize the geofence',
                                    value: 'false'
                                }
                            ]}
                            direction={'column'}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateGeofenceVisibility(e.target.value === 'true')
                            }
                            checked={hasDefaultGeofence.toString()}
                        />
                    </div>

                    <div
                        className={css`
                            display: flex;
                            flex-direction: column;
                            flex: 1;

                            & > div {
                                margin: 10px 0px;
                            }
                        `}
                        style={{
                            backgroundColor: theme.lightGrey,
                            padding: '0px 8px',
                            borderRadius: '10px',
                            opacity: hasDefaultGeofence ? 0.5 : 1
                        }}>
                        <div>
                            <Input
                                label={`Set the task radius in meters (a perimeter around the task location)`}
                                type="number"
                                step="25"
                                min="50"
                                placeholder="75"
                                value={taskRadius.metres}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    updateRadius(e.target.value)
                                }
                                disabled={hasDefaultGeofence}
                            />
                        </div>

                        <div>
                            <label>
                                Should the task attempt to complete when crossing the geofence?
                            </label>
                            <RadioGroup
                                name={'completion'}
                                options={[
                                    {
                                        label: 'No',
                                        value: 'manual'
                                    },
                                    {
                                        label: 'Yes',
                                        value: 'none'
                                    }
                                ]}
                                direction={'row'}
                                onChange={(e: any) => updateCompletionMethod(e.target.value)}
                                checked={completionMethod}
                                disabled={hasDefaultGeofence}
                            />
                        </div>

                        <div>
                            <label>
                                Should drivers be prevented from completing the task if they are on
                                the wrong side of the geofence?
                            </label>
                            <RadioGroup
                                name={'manual-constraint'}
                                options={[
                                    {
                                        label: 'No',
                                        value: 'none'
                                    },
                                    {
                                        label: 'Yes',
                                        value: 'region'
                                    }
                                ]}
                                direction={'row'}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    updateConstraints(e.target.value)
                                }
                                checked={taskConstraints}
                                disabled={hasDefaultGeofence}
                            />
                        </div>
                    </div>

                    <div>
                        <label>Will this task have a deadline?</label>
                        <RadioGroup
                            name={'timeframe'}
                            options={[
                                {
                                    label: `No`,
                                    value: 'mask'
                                },
                                {
                                    label: 'Yes',
                                    value: 'source'
                                }
                            ]}
                            direction={'row'}
                            onChange={(e: any) => updateTimeframe(e.target.value)}
                            checked={editedTaskTemplate.timeframe}
                        />
                    </div>

                    {editedTaskTemplate.timeframe == 'source' ? (
                        <div
                            className={css`
                                flex: 1;
                            `}>
                            <Input
                                optional
                                label="Deadline (number of minutes after provided deadline)"
                                type="number"
                                step="1"
                                min="0"
                                placeholder="0"
                                value={
                                    editedTaskTemplate.offset
                                        ? editedTaskTemplate.offset / 60000
                                        : ''
                                }
                                onChange={(e: any) => updateOffset(e.target.value)}
                            />
                        </div>
                    ) : null}

                    <div
                        className={css`
                            display: flex;
                            flex-direction: column;
                        `}>
                        <label>
                            Is there anything a driver must do before completing this task?
                        </label>
                        {CONSTRAINT_OPTIONS.map((c) => {
                            const checked =
                                editedTaskTemplate.constraints &&
                                editedTaskTemplate.constraints.has(c.value);

                            return (
                                <Checkbox
                                    key={c.value}
                                    checked={checked}
                                    onChange={() =>
                                        runInAction(() => checked
                                            ? editedTaskTemplate.constraints.delete(c.value)
                                            : editedTaskTemplate.constraints.add(c.value)
                                        )}
                                    label={c.label}
                                    description={c.description}
                                />
                            );
                        })}
                    </div>
                </div>
            </ModalContent>
            <ModalActions>
                <Button onClick={cancel}>Cancel</Button>
                <Button primary onClick={saveTask}>
                    Save
                </Button>
            </ModalActions>
        </>
    );
});
